import { getQueryParam } from "@/utils";

const SCRIPT_ID = "cpt-widget";
const SCRIPT_URL = "https://captainup.com/cpt-widget.js";
const SCRIPT_VERSION = "8.4.25";
const SCRIPT_VERSION_QUERY_PARAM = "captainupSdkVersion";
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

let loadPromise: Promise<void> | null = null;

type WindowWithCaptainUpTestFlags = Window & {
	__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__?: boolean;
};

function normalizeScriptVersion(version: string | null): string | null {
	const normalizedVersion = (version ?? "").trim();
	return normalizedVersion.length > 0 ? normalizedVersion : null;
}

function resolveCaptainUpScriptVersion(): string {
	const version = normalizeScriptVersion(getQueryParam(SCRIPT_VERSION_QUERY_PARAM));
	if (version) {
		return version;
	}

	return SCRIPT_VERSION;
}

function ensureFontLink(href: string): void {
	const head = document.head || document.getElementsByTagName("head")[0];
	if (!head) return;

	const existing = Array.from(head.getElementsByTagName("link")).find((l) => l.href === href);
	if (existing) return;

	const link = document.createElement("link");
	link.rel = "stylesheet";
	link.href = href;
	head.appendChild(link);
}

function consumeCaptainUpSdkLoadFailureCheat(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const targetWindow = window as WindowWithCaptainUpTestFlags;
	const shouldFail = targetWindow.__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__ === true;

	if (shouldFail) {
		targetWindow.__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__ = false;
	}

	return shouldFail;
}

export async function loadCaptainUpScript(): Promise<void> {
	if (loadPromise) return loadPromise;

	loadPromise = new Promise<void>((resolve, reject) => {
		if (consumeCaptainUpSdkLoadFailureCheat()) {
			reject(new Error("[CaptainUpLoader] cheat-triggered SDK load failure"));
			return;
		}

		const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

		let settled = false;
		let timeoutId: number | null = null;

		const finishResolve = () => {
			if (settled) return;
			settled = true;

			if (timeoutId !== null) {
				window.clearTimeout(timeoutId);
			}

			resolve();
		};

		const finishReject = (error: Error) => {
			if (settled) return;
			settled = true;

			if (timeoutId !== null) {
				window.clearTimeout(timeoutId);
			}

			loadPromise = null;
			reject(error);
		};

		timeoutId = window.setTimeout(() => {
			finishReject(new Error("[CaptainUpLoader] script load timed out"));
		}, SCRIPT_LOAD_TIMEOUT_MS);

		if (existing) {
			const check = () => {
				if (typeof window.cptLoadWidget === "function") {
					finishResolve();
					return;
				}

				if (settled) {
					return;
				}

				requestAnimationFrame(check);
			};

			check();
			return;
		}

		ensureFontLink(
			"https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap"
		);

		const script = document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = SCRIPT_URL;

		// Vendor expects these attributes.
		script.dataset.widget = "widget-popup9";
		script.dataset.version = resolveCaptainUpScriptVersion();

		script.onload = () => {
			if (typeof window.cptLoadWidget !== "function") {
				finishReject(new Error("[CaptainUpLoader] script loaded but window.cptLoadWidget is not a function"));
				return;
			}

			finishResolve();
		};

		script.onerror = () => {
			finishReject(new Error("[CaptainUpLoader] failed to load CaptainUp widget script"));
		};

		document.body.appendChild(script);
	});

	return loadPromise;
}
